import { ExtensionWindow, RenderData, QueryResults, CustomParams } from "../../types";
import { getLocale } from "@omegagrid/localize";
import { ComponentId, utils } from "@omegagrid/core";
import type { Engine } from "../engine";
import type { HtmlAdapterV2 } from "./htmlController-v2";
import type { Container } from '@reporting/rp-app-host/src/container/container';

export type ExtensionV2Window = ExtensionWindow & {
	rc: LegacyAdapter;
	ejs: {render: (template: string, data: RenderData, options?: any) => string};
	Reporting: {
		Custom: typeof LegacyAdapter;
		container: Container;
	}
}

type LegacyAdapterEventHandler = (...args: any[]) => void;

type LegacyAdapterRegistry = {
	links: Record<string, string>;
}

const wrapExport = (html: string) => `<html><head><meta http-equiv="content-type" content="text/html;charset=utf-8" /></head><body>${html}</body></html>`;

export class LegacyAdapter {

	renderArgs: {template: string, data: Record<string, any>, target: string|HTMLElement};

	/**
	 * Main template content. Legacy API contract: an extension may set it to null
	 * (typically in its 'init' handler) to suppress rendering until it is ready,
	 * then restore the value and call refresh().
	 */
	template: string | null;

	private _eventHandlers: Record<string, Array<LegacyAdapterEventHandler>> = {};
	get eventHandlers() { return this._eventHandlers }

	private _registry: LegacyAdapterRegistry = { links: {} };
	get registry() { return this._registry }

	private _engine: Engine;
	get engine() { return this._engine }
	set engine(value: Engine) { this._engine = value }

	private _data: Record<string, QueryResults>;
	get data() {
		if (this._data) return this._data;
		this._data = {};
		for (const [key, value] of this.engine.data) {
			this._data[key] = value;
		}
		return this._data;
	}

	private _params: CustomParams = {};
	set params(values: CustomParams) { this._params = values }
	get params() { return this._params }

	get window() { return (this._engine?.window ?? window) as any as ExtensionV2Window }
	get document() { return this.window?.document }

	public escape: (str: string) => string;

	private _locale: string;
	get locale() {
		if (!this._locale) this._locale = getLocale();
		return this._locale;
	}
	set locale(value: string) { this._locale = value }

	loader(elm: HTMLElement, text?: string) {
		const loader = this.document.createElement('div');
		loader.innerHTML = `
			<div style="text-align: center; margin-top: 100px">
				<img src="${this.engine.options.staticUrl}/assets/loader.webp" alt="loader" style="width: 40px; height: 40px" />
				${text ? `<div>${text}</div>` : ''}
			</div>
		`;
		elm.appendChild(loader);
	}

	on(name: string, handler: (...args: any[]) => void) {
		this._eventHandlers[name] = this._eventHandlers[name] || [];
		this._eventHandlers[name].push(handler);
	}

	off(name: string, handler?: (...args: any[]) => void) {
		if (handler && Array.isArray(this._eventHandlers[name])) {
			const idx = this._eventHandlers[name].indexOf(handler);
			if (idx > -1) this._eventHandlers[name].splice(idx, 1);
		} else {
			this._eventHandlers[name] = [];
		}
		return this;
	}

	fire(name: string, ...args: any[]) {
		let result = null;
		if (this._eventHandlers[name]) {
			for (var i = 0; i < this._eventHandlers[name].length; i++) {
				const callback = this._eventHandlers[name][i];
				result = callback.apply(this, args);
			}
		}
		return result;
	}

	getResource(name: string, callback: (content: string) => void) {
		this.engine.getResource(name, true).then(content => {
			callback(content);
		});
	}

	invokeRender() {
		if (!this.renderArgs) return;
		this.renderTemplate(this.renderArgs.template, this.renderArgs.target, this.renderArgs.data);
		this.renderArgs = null;
	}

	private _renderer = (template: string, data: Record<string, any>) => {
		if (!this.window.ejs) return template;
		return this.window.ejs.render(template, {rc: this, ...data}, {escape: this.escape});
	}

	get renderer() {
		return this._renderer;
	}

	execAll(resource: string, cb?: (data: Record<string, any>) => void) {
		this.engine.dataRelay.execResource(this.engine.getResourceId(resource), this.params).then(results => {
			results.forEach((result, name) => this.data[name] = result);
			if (cb) cb(this.data);
		});
	}

	exec(name: string | string[], ...args: any[]) {
		let params: CustomParams = null;
		let success: (data: any) => void = null;
		let error: (err: any) => void = null;

		for (var i = 0; i < args.length; i++) {
			const arg = args[i];
			if (typeof arg === 'string' && ['POST', 'GET'].indexOf(arg.toUpperCase()) > -1) {
				// method is ignored - the new data relay decides the transport
			} else if (utils.isFunction(arg)) {
				if (success == null) success = arg;
				else error = arg;
			} else if (utils.isObject(arg)) {
				params = arg as CustomParams;
			}
		}

		const names = Array.isArray(name) ? name : [name];
		const mergedParams = { ...this.params, ...(params || {}) };

		const tasks = names.map(n => {
			const key = String(n).replace(/\.sql$/i, '');
			const idQuery = this.engine.getQueryId(n);
			if (idQuery == null) {
				return Promise.reject(new Error(`Query not found: ${n}`));
			}
			return this.engine.dataRelay.exec(idQuery, undefined, mergedParams).then(result => {
				this.data[key] = result;
				return [key, result] as [string, QueryResults];
			});
		});

		return Promise.all(tasks).then(results => {
			let data: any;
			if (Array.isArray(name)) {
				data = {};
				for (const [key, result] of results) data[key] = result;
			} else {
				data = results[0][1];
			}
			if (success) success(data);
			return data;
		}, err => {
			if (error) error(err);
			else throw err;
		});
	}

	render(resource: string, ...args: any[]) {
		let elm: HTMLElement;
		let cb: (...args: any[]) => void;
		let data: Record<string, any>;

		for (var i = 0; i < args.length; i++) {
			if (args[i] && args[i].nodeType === 1) {
				elm = args[i];
			} else if (typeof args[i] == 'string') {
				elm = this.document.querySelector(args[i]);
			} else if (utils.isFunction(args[i])) {
				cb = args[i];
			} else if (utils.isObject(args[i])) {
				data = args[i];
			}
		}

		if (elm && elm.offsetHeight > 200) {
			this.loader(elm);
		}
		
		this.getResource(resource, content => {
			this.execAll(resource, () => {
				if (elm) this.renderTemplate(content, elm, data);
				if (cb) cb(content);
			});
		});
	}

	renderTemplate(template: string, target: string|HTMLElement, data: Record<string, any>) {
		const targetElement = typeof target == 'string' ? this.engine.containerElement.querySelector(target) as HTMLElement : target;
		if (targetElement) {
			targetElement.innerHTML = this.renderer(template, data);
			this.engine.evaluateScripts(targetElement);
		}
	}

	link(...args: any[]) {
		// let view: string;
		// let params: Record<string, any>;

		// for (var i = 0; i < args.length; i++) {
		// 	if (typeof args[i] === 'string') {
		// 		view = args[i];
		// 	} else if (utils.isObject(args[i])) {
		// 		params = args[i];
		// 	}
		// }

		// var id = this.id;
		// var view2 = this.view;

		// if (view) {
		// 	var parts = view.split(':');
		// 	id = parts.length > 1 ? parts[0] : this.id;
		// 	view2 = parts.length > 1 ? parts[1] : parts[0];
		// }

		// var url = this.registry.links.page;
		// url = url.replace('<page>', id).replace('<resource>', view2);

		// if (this.params) {
		// 	for (var k in this.params) {
		// 		params = params || {};
		// 		if (typeof params[k] === 'undefined') params[k] = this.params[k]; 
		// 		if (params[k] === null) delete params[k];

		// 	}
		// }

		// if (params) {
		// 	url += (url.indexOf('?') == -1 ? '?' : '&') + this.encodeParams(params);
		// }

		console.warn(`link() not implemented (args: ${JSON.stringify(args || [])})`);
		return '';
	}

	refresh() {
		(this.engine.adapter as HtmlAdapterV2).refresh();
	}


	export(opts: string|Record<string, any>, type?: string, content?: string) {
		let values: Record<string, any> = {};
		let options: Record<string, any> = {};

		if (typeof opts === "string") {
			values = {filename: options, type: type, content: content};
		} else {
			options = (opts as Record<string, any>) || {};

			for (var k in options) {
				if (k.toLowerCase() != 'elm') values[k] = options[k];
			}

			if (!values.content && !values.url) {
				var elm = null;
				if (options.elm && typeof options.elm == 'string') {
					elm = document.getElementById(options.elm);
				} else if (options.elm) {
					elm = options.elm;
				} else {
					elm = document.getElementsByTagName('body')[0];
				}
				values.content = elm.tagName == 'BODY' ? elm.innerHTML : elm.outerHTML;
			}
		}

		if (!options.hasOwnProperty('html')) {
			options.html = true;
		} 

		if (options.html && !options.url) {
			// removes elements with noexport class
			var div = document.createElement('div');
			div.innerHTML = values.content;
			var noexport = div.getElementsByClassName('noexport');

			while (noexport[0]) {
				noexport[0].parentNode.removeChild(noexport[0]);
			}

			values.content = wrapExport(div.innerHTML);
		}

		options.service = options.service || this.registry.links.export;

		var result = this.fire('beforeExport', values);
		if (result === false) return;

		var form = document.createElement("form");
		// form.setAttribute('target', '_blank');
		form.setAttribute('method', 'post');
		form.setAttribute('action', options.service);

		for (var name in values) {
			var input = document.createElement('input');
			input.setAttribute('type', 'hidden');
			input.setAttribute('name', name);
			if (utils.isObject(values[name])) {
				input.setAttribute('value', JSON.stringify(values[name]));
			} else {
				input.setAttribute('value', values[name]);
			}
			form.appendChild(input);
		}

		document.body.appendChild(form);
		form.submit();
	};

	broadcast(message: unknown) {
		this.engine.broadcast({content: message});
	}

	error(message: string) {
		console.error(message);

		const div = this.document.createElement('div');
		div.style.color = '#f00';
		div.innerText = message;

		const body = this.document.getElementsByTagName('body')[0];

		if (body.children.length > 0) {
			body.insertBefore(div, body.firstChild);
		} else {
			body.appendChild(div);
		}
	}

	public getLink(name: string, params?: Record<string, any>) {
		let link = this.registry.links[name];
		Object.keys(params || {}).forEach(key => {
			const value = params[key];
			link = link.replace(`<${key}>`, value);
		});
		return link;
	}

	resource(nameOrId: ComponentId) {
		return this.getLink('resource', {resource: nameOrId.toString()});
	}

	file(id: ComponentId) {
		return this.getLink('file', {file: id.toString()});
	}

}
